import { Sun, Sunrise, Sunset } from 'lucide-react';
import Section from './Section.jsx';
import { planParagraphs } from '../lib/plan.js';
import { sentenceCase } from '../lib/format.js';
import { usePersonaPulse } from '../lib/use-persona-pulse.js';

// The lead story of the brief: the guest's day, planned chronologically in the
// host voice. Current briefs send three paragraphs separated by blank lines;
// older archived briefs send a single block, which renders as one paragraph.
//
// Set on paper like every other section, in the prose role, across the full
// column (design review 2026-09-26, finding 6). The rounded cream box around
// it cut the measure to 308px on a phone, 34 to 36 characters a line, when the
// guides run 39 to 43, and it was a surface the 2026-09-20 rule never named.
//
// When the brief meets the three-paragraph contract (src/lib/plan.js), the
// top picks move in here: each daypart gets its pick as a subhead over its own
// paragraph (item C1). The lead then carries no separate picks list.

// Skimmers extract the plan from place+time anchors. New briefs wrap those in
// **double asterisks** (see synthesis prompt); briefs from before that change
// have no markers, so fall back to bolding bare clock times.
const TIME_PATTERN = /(\b\d{1,2}(?::\d{2})?\s?(?:AM|PM)\b)/g;

function richParagraph(p) {
  const pattern = p.includes('**') ? /\*\*(.+?)\*\*/g : TIME_PATTERN;
  const parts = p.split(pattern);
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold">
        {part}
      </strong>
    ) : (
      part
    )
  );
}

// A small gold glyph for each daypart, the same sun that marks sunrise and
// sunset on the day strip above the list.
const DAYPART_ICONS = { morning: Sunrise, afternoon: Sun, evening: Sunset };

// `strip` is the day's tide-and-daylight drawing (DayStrip.jsx), set between
// the heading and the plan it frames.
export default function HostNote({ text, dayparts, personaLabel, strip = null }) {
  const pulse = usePersonaPulse(personaLabel);
  if (dayparts) {
    return (
      <Section id="plan" eyebrow="The plan" title="How to play today">
        {strip}
        {/* A daypart with no pick (the afternoon, most days) is not a gap: its
            paragraph is the plan for that stretch, so it stands on its own.
            Labels are navy small caps like the lead's were; gold stays with
            structural kickers. At sm+ the label takes a column of its own and
            the story keeps a reading measure beside it. */}
        <ol
          className={[
            '-mx-2 divide-y divide-navy-100 px-2 transition-colors duration-300 motion-reduce:transition-none',
            pulse ? 'bg-cream-100' : 'bg-transparent'
          ].join(' ')}
        >
          {dayparts.map((d) => (
            <li
              key={d.id}
              className="py-5 first:pt-1 last:pb-1 sm:grid sm:grid-cols-[7rem_minmax(0,1fr)] sm:gap-6"
            >
              <h3 className="flex items-center gap-1.5 self-start text-[11px] font-semibold uppercase tracking-[0.16em] text-navy-500 sm:pt-[5px]">
                {(() => {
                  const Icon = DAYPART_ICONS[d.id];
                  return Icon ? <Icon className="h-4 w-4 shrink-0 text-cream-500" aria-hidden="true" /> : null;
                })()}
                {d.label}
              </h3>
              <div>
                {/* The pick is the subhead, so it takes the display face. */}
                {d.pick && (
                  <p className="mt-1 font-display text-xl font-semibold leading-snug text-navy-900 sm:mt-0">
                    {sentenceCase(d.pick)}
                  </p>
                )}
                <p className={['prose-navy text-navy-900', d.pick ? 'mt-2' : 'mt-1 sm:mt-0'].join(' ')}>
                  {richParagraph(d.paragraph)}
                </p>
              </div>
            </li>
          ))}
        </ol>
      </Section>
    );
  }

  const paragraphs = planParagraphs(text);
  if (!paragraphs.length) return null;
  return (
    <Section id="plan" eyebrow="The plan" title="How to play today">
      {strip}
      <div className="prose-navy space-y-4 text-navy-900">
        {paragraphs.map((p, i) => (
          <p key={i}>{richParagraph(p)}</p>
        ))}
      </div>
    </Section>
  );
}
